/**
 * Tiny audio service for in-game SFX.
 *
 * Drop your audio files into `frontend/public/sounds/` with these exact
 * names — the player picks them up at runtime, no rebuild needed:
 *
 *   click.mp3        — generic UI click / button press
 *   win.mp3          — any positive outcome (box win, fast win, battle win)
 *   lose.mp3         — any negative outcome (fast miss, battle lose)
 *   box-open.mp3     — BSP BOXES roulette running
 *   big-win.mp3      — BIG GIVEAWAY / 667 jackpot (heavier than win.mp3)
 *   coin.mp3         — deposit credited, balance grant arrived
 *   notify.mp3       — bell ring on new notification
 *   battle-roll.mp3  — battle settlement dice roll
 *
 * Files missing? `play()` is a no-op and never throws. Volume defaults to
 * 0.5; the user can mute everything by toggling `enabled` (persisted to
 * localStorage so the choice survives reloads).
 */

type SfxName =
    | 'click'
    | 'win'
    | 'lose'
    | 'box-open'
    | 'big-win'
    | 'coin'
    | 'notify'
    | 'battle-roll';

const ENABLED_KEY = 'bogstars:sfx';
const VOLUME_KEY = 'bogstars:sfx-vol';

let enabled = (() => {
    try { return localStorage.getItem(ENABLED_KEY) !== '0'; } catch { return true; }
})();
let volume = (() => {
    try {
        const v = Number(localStorage.getItem(VOLUME_KEY));
        return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.5;
    } catch { return 0.5; }
})();

// Cache HTMLAudioElement per file so repeated plays don't re-download.
// Telegram's WebView throttles concurrent decodes, so we clone the cached
// element on each play — overlapping sounds work, the cache just avoids
// re-fetching the .mp3 over the network.
const cache = new Map<SfxName, HTMLAudioElement>();

function load(name: SfxName): HTMLAudioElement | null {
    if (cache.has(name)) return cache.get(name)!;
    try {
        const el = new Audio(`/sounds/${name}.mp3`);
        el.preload = 'auto';
        cache.set(name, el);
        return el;
    } catch {
        return null;
    }
}

export function play(name: SfxName): void {
    if (!enabled) return;
    const base = load(name);
    if (!base) return;
    try {
        const node = base.cloneNode(true) as HTMLAudioElement;
        node.volume = volume;
        const p = node.play();
        // Ignore the auto-play rejection — Telegram needs a user gesture
        // first, after which subsequent plays work.
        if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
        /* never crash the UI for a missing sound */
    }
}

export function setEnabled(on: boolean): void {
    enabled = on;
    try { localStorage.setItem(ENABLED_KEY, on ? '1' : '0'); } catch { /* private mode */ }
}

export function isEnabled(): boolean {
    return enabled;
}

export function setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    volume = clamped;
    try { localStorage.setItem(VOLUME_KEY, String(clamped)); } catch { /* private mode */ }
}

export function getVolume(): number {
    return volume;
}

// Pre-warm the most common files so the first play has zero network
// latency. Called once at app boot.
export function prewarm(): void {
    (['click', 'win', 'lose', 'box-open', 'coin'] as SfxName[]).forEach(load);
}
